import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Truck, ShieldCheck, MessageCircle, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import CategoryStrip from '@/components/CategoryStrip';
import CollectionsStrip from '@/components/CollectionsStrip';
import BannerCarousel from '@/components/BannerCarousel';
import ProductRow from '@/components/ProductRow';
import GalleryLooks from '@/components/GalleryLooks';
import TestimonialsSection from '@/components/TestimonialsSection';
import FinanceEducation from '@/components/FinanceEducation';
import { useSEO } from '@/hooks/useSEO';
import { CATEGORIES } from '@/mock/products';
import { useAvailableProducts, useCategories, useBanners } from '@/store/dataStore';
import { useLabels } from '@/services/labelsService';

export default function HomePage() {
  const { products: PRODUCTS } = useAvailableProducts();
  const { categories } = useCategories();
  const { banners } = useBanners();
  useSEO({
    title: 'BIGTIME · Todo en un solo lugar · Venta al mayor Venezuela',
    description: 'BIGTIME: relojería, belleza, bolsos, accesorios, electrónicos al mayor desde 1 pieza en Caracas, Maracaibo y Valencia. Precios mejores que Alibaba, envíos en 24-72h, garantía con video.',
    keywords: 'venta al mayor Venezuela, mayorista relojes, mayorista belleza, alibaba Venezuela alternativa, bigtime',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'BIGTIME',
      url: (typeof window !== 'undefined' ? window.location.origin : 'https://bigtimeca.com'),
      logo: (typeof window !== 'undefined' ? window.location.origin : 'https://bigtimeca.com') + '/brand/bigtime-mono.png',
      sameAs: ['https://www.tiktok.com/@bigtimeca', 'https://www.instagram.com/bigtimeca'],
      address: { '@type': 'PostalAddress', addressCountry: 'VE' },
    },
  });
  const activeBanners = (banners || []).filter((b) => b.active === true || (b.active === undefined && !String(b.id || '').startsWith('b_')));
  const CATS = categories && categories.length ? categories : CATEGORIES;
  const { label: L } = useLabels();
  const featured = PRODUCTS.filter((p) => p.isFeatured).slice(0, 10);
  const newArrivals = PRODUCTS.filter((p) => p.isNew).slice(0, 10);
  const norm = (v) => String(v || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
  const catKey = (c) => [norm(c.id), norm(c.name)];
  const inCat = (p, c) => catKey(c).includes(norm(p.category));
  const categoryRows = CATS.map((c) => ({ cat: c, items: PRODUCTS.filter((p) => inCat(p, c)) }));
  const orphans = PRODUCTS.filter((p) => !CATS.some((c) => inCat(p, c)));
  if (orphans.length) categoryRows.push({ cat: { id: 'otros', name: 'Otros productos' }, items: orphans });
  const interludes = { 0: <GalleryLooks key="gl" />, 2: <TestimonialsSection key="ts" />, 4: <FinanceEducation key="fe" /> };

  return (
    <div>
      {/* HERO — dynamic banners from Admin if available; falls back to default */}
      {activeBanners.length > 0 ? (
        <section className="bg-[#F8D13F] pt-6 pb-2" data-testid="hero-dynamic-banners">
          <BannerCarousel />
        </section>
      ) : (
      <section className="relative bg-[#F8D13F] overflow-hidden" data-testid="hero-default">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, #1B2A4A 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
        <div className="relative max-w-7xl mx-auto px-4 py-8 md:py-20 grid md:grid-cols-2 gap-6 md:gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-1.5 bg-[#1B2A4A] text-yellow-300 text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider mb-5">
              <Sparkles size={13} /> Nueva colección disponible
            </span>
            <h1 className="text-5xl md:text-7xl font-extrabold text-[#1B2A4A] leading-[0.95] tracking-tight" style={{ fontFamily: "'Fredoka', system-ui" }}>
              BIGTIME<span className="text-[#1B2A4A]">.</span>
            </h1>
            <p className="mt-3 text-2xl md:text-3xl font-semibold text-[#1B2A4A]">Todo en <span className="underline decoration-4 decoration-white underline-offset-4">un solo lugar</span>.</p>
            <p className="mt-4 text-[#1B2A4A]/80 text-base md:text-lg max-w-lg">
              Relojería, belleza, bolsos, electrónicos y mucho más. Compra fácil y recibe tu pedido directo por WhatsApp.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/catalogo">
                <Button className="bg-[#1B2A4A] hover:bg-[#141f38] text-yellow-300 rounded-full h-12 px-6 font-bold">
                  Ver catálogo <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
              <a href="https://wa.me/584123120579" target="_blank" rel="noreferrer">
                <Button variant="outline" className="border-2 border-[#1B2A4A] bg-transparent text-[#1B2A4A] hover:bg-[#1B2A4A] hover:text-yellow-300 rounded-full h-12 px-6 font-bold">
                  <MessageCircle size={18} className="mr-2" /> Pedir por WhatsApp
                </Button>
              </a>
            </div>
          </div>

          <div className="relative hidden md:block">
            <div className="aspect-square max-w-md mx-auto relative">
              <div className="absolute inset-0 bg-[#1B2A4A] rounded-[3rem] rotate-6" />
              <div className="absolute inset-0 bg-white rounded-[3rem] -rotate-3 shadow-2xl overflow-hidden">
                <img loading="lazy" decoding="async" src="https://images.pexels.com/photos/33797659/pexels-photo-33797659.jpeg?auto=compress&cs=tinysrgb&w=800" alt="Ventas al mayor Venezuela · Relojería, belleza y accesorios BIGTIME desde 1 pieza · Caracas y Maracaibo" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#F8D13F] border-4 border-[#1B2A4A] rounded-2xl px-4 py-3 shadow-xl rotate-[-8deg]">
                <div className="text-[10px] font-bold text-[#1B2A4A] uppercase">Envío</div>
                <div className="text-lg font-extrabold text-[#1B2A4A] leading-none">Nacional</div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave separator */}
        <svg className="block w-full text-white" viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ height: 40 }}>
          <path d="M0,32 C240,80 480,0 720,24 C960,48 1200,8 1440,32 L1440,60 L0,60 Z" fill="currentColor" />
        </svg>
      </section>
      )}

      {/* Benefits */}
      <section className="max-w-7xl mx-auto px-3 md:px-4 -mt-2">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
          {[
            { icon: Truck, title: 'Envíos rápidos', text: 'A todo Venezuela' },
            { icon: ShieldCheck, title: 'Compra segura', text: 'Productos garantizados' },
            { icon: MessageCircle, title: 'Atención personal', text: 'Pedido por WhatsApp' },
            { icon: Sparkles, title: 'Nuevos ingresos', text: 'Cada semana' },
          ].map((b) => (
            <div key={b.title} className="bg-white border border-slate-200 rounded-xl p-3 md:p-4 flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-3">
              <div className="w-9 h-9 md:w-11 md:h-11 rounded-full bg-yellow-100 text-[#1B2A4A] flex items-center justify-center flex-shrink-0">
                <b.icon size={18} />
              </div>
              <div>
                <div className="text-[13px] md:text-sm font-bold text-[#1B2A4A] leading-tight">{b.title}</div>
                <div className="text-[11px] md:text-xs text-slate-500 leading-snug">{b.text}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <CategoryStrip />

      <CollectionsStrip />

      <ProductRow title="Productos Destacados" products={featured} categoryHref="/catalogo" testid="home-featured-row" />

      {/* Promo band */}
      <section className="my-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="rounded-3xl bg-[#1B2A4A] text-white overflow-hidden relative p-5 md:p-12 grid md:grid-cols-2 gap-6 items-center">
            <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-yellow-300/10" />
            <div className="absolute -right-8 -bottom-8 w-40 h-40 rounded-full bg-yellow-300/10" />
            <div className="relative">
              <span className="inline-block bg-[#F8D13F] text-[#1B2A4A] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">Ofertas de la semana</span>
              <h3 className="text-3xl md:text-4xl font-extrabold mt-3">Los mejores precios en <span className="text-yellow-300">un solo click</span></h3>
              <p className="text-white/70 mt-2">Descubre nuestro catálogo con productos seleccionados para ti.</p>
              <Link to="/catalogo" className="inline-block mt-5">
                <Button className="bg-[#F8D13F] text-[#1B2A4A] hover:bg-yellow-300 rounded-full h-11 px-6 font-bold">Explorar ahora <ArrowRight size={16} className="ml-2" /></Button>
              </Link>
            </div>
            <div className="relative grid grid-cols-3 gap-3">
              {CATS.slice(0, 6).map((c) => (
                <Link key={c.id} to={`/catalogo?cat=${c.id}`} className="bg-white/10 hover:bg-white/20 border border-white/10 rounded-xl p-3 text-center text-xs font-semibold transition">
                  {L(`category.${c.id}`, c.name)}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <ProductRow title="Nuevos Ingresos" products={newArrivals} categoryHref="/catalogo" testid="home-new-row" />
      {categoryRows.map((r, idx) => (
        <React.Fragment key={r.cat.id}>
          <ProductRow title={L(`category.${r.cat.id}`, r.cat.name)} products={r.items.slice(0, 20)} count={r.items.length}
            categoryHref={r.cat.id === 'otros' ? '/catalogo' : `/catalogo?cat=${r.cat.id}`} testid={`home-cat-row-${r.cat.id}`} showEmpty />
          {interludes[idx]}
        </React.Fragment>
      ))}
      {Object.entries(interludes).filter(([k]) => Number(k) >= categoryRows.length).map(([, el]) => el)}
    </div>
  );
}
